import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const TopupPage = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [packages, setPackages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [currentTransactionId, setCurrentTransactionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [midtransClientKey, setMidtransClientKey] = useState('');
  const [isProduction, setIsProduction] = useState(false);
  const [, setTick] = useState(0); // For countdown refresh

  // Countdown timer - refresh every second
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Format currency
  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date - ensure proper timezone handling
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    // Parse the date string - add 'Z' if no timezone indicator to treat as UTC
    let date;
    if (dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-', 10)) {
      date = new Date(dateString);
    } else {
      // Assume UTC if no timezone indicator
      date = new Date(dateString + 'Z');
    }
    
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [packagesRes, transactionsRes, clientKeyRes] = await Promise.all([
        axios.get(`${API}/token-packages`),
        axios.get(`${API}/token-transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/midtrans/client-key`)
      ]);
      
      setPackages(packagesRes.data || []);
      setTransactions(transactionsRes.data || []);
      setMidtransClientKey(clientKeyRes.data.client_key);
      setIsProduction(clientKeyRes.data.is_production);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      // Just fetch data without auto-cancelling pending transactions
      fetchData();
    }
  }, [token, fetchData]);

  // Check for payment status from redirect
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'finish') {
      toast.success('Terima kasih! Pembayaran sedang diproses.');
      fetchData();
      if (refreshUser) refreshUser();
    }
  }, [searchParams, fetchData, refreshUser]);

  // Load Midtrans Snap script
  useEffect(() => {
    if (midtransClientKey) {
      const scriptId = 'midtrans-snap-script';
      const existingScript = document.getElementById(scriptId);
      
      if (!existingScript) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = isProduction 
          ? 'https://app.midtrans.com/snap/snap.js'
          : 'https://app.sandbox.midtrans.com/snap/snap.js';
        script.setAttribute('data-client-key', midtransClientKey);
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }, [midtransClientKey, isProduction]);

  // Handle package selection and payment
  const handleTopup = async (pkg) => {
    if (processing) return;
    
    setSelectedPackage(pkg);
    setProcessing(true);
    
    try {
      const response = await axios.post(`${API}/topup/create`, 
        { package_id: pkg.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { snap_token, transaction_id } = response.data;
      setCurrentTransactionId(transaction_id);
      
      // Open Midtrans Snap popup
      openSnapPayment(snap_token, transaction_id);
      
    } catch (error) {
      console.error('Error creating transaction:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat transaksi');
      setProcessing(false);
    }
  };

  // Resume pending payment
  const handleResumePending = async (trans) => {
    if (processing) return;
    
    setProcessing(true);
    setCurrentTransactionId(trans.id);
    
    try {
      const response = await axios.post(`${API}/topup/resume/${trans.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { snap_token } = response.data;
      
      // Open Midtrans Snap popup
      openSnapPayment(snap_token, trans.id);
      
    } catch (error) {
      console.error('Error resuming payment:', error);
      const errorMsg = error.response?.data?.detail || 'Gagal melanjutkan pembayaran';
      toast.error(errorMsg);
      fetchData(); // Refresh to update status if expired
      setProcessing(false);
      setCurrentTransactionId(null);
    }
  };

  // Verify payment and update balance
  const verifyPayment = async (transactionId) => {
    try {
      const response = await axios.post(`${API}/topup/verify/${transactionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'success') {
        toast.success(`Pembayaran berhasil! +${response.data.tokens_added} Token`);
        if (refreshUser) refreshUser();
      }
      
      return response.data;
    } catch (error) {
      console.error('Error verifying payment:', error);
      return null;
    }
  };

  // Open Midtrans Snap payment popup
  const openSnapPayment = (snap_token, transactionId) => {
    if (window.snap) {
      try {
        window.snap.pay(snap_token, {
          onSuccess: async function(result) {
            console.log('Payment success:', result);
            toast.loading('Memverifikasi pembayaran...');
            
            // Verify payment and update balance
            await verifyPayment(transactionId);
            
            fetchData();
            setProcessing(false);
            setSelectedPackage(null);
            setCurrentTransactionId(null);
          },
          onPending: async function(result) {
            console.log('Payment pending:', result);
            
            // Still try to verify - some payment methods complete instantly
            const verifyResult = await verifyPayment(transactionId);
            
            if (verifyResult?.status !== 'success') {
              toast('Menunggu pembayaran...', { icon: 'ℹ️' });
            }
            
            fetchData();
            setProcessing(false);
            setSelectedPackage(null);
            setCurrentTransactionId(null);
          },
          onError: function(result) {
            console.log('Payment error:', result);
            toast.error('Pembayaran gagal');
            setProcessing(false);
            setSelectedPackage(null);
            setCurrentTransactionId(null);
          },
          onClose: async function() {
            console.log('Payment popup closed');
            
            // Try to verify - maybe payment completed
            const verifyResult = await verifyPayment(transactionId);
            
            // If not successful, just notify user - don't auto-cancel
            if (!verifyResult || verifyResult.status !== 'success') {
              toast('Pembayaran belum selesai. Anda bisa melanjutkan atau membatalkan transaksi.', { icon: 'ℹ️' });
            }
            
            fetchData();
            setProcessing(false);
            setSelectedPackage(null);
            setCurrentTransactionId(null);
          }
        });
      } catch (snapError) {
        console.error('Snap error:', snapError);
        toast.error('Gagal membuka popup pembayaran. Silakan refresh halaman.');
        setProcessing(false);
        setCurrentTransactionId(null);
      }
    } else {
      toast.error('Payment gateway belum siap. Silakan refresh halaman.');
      setProcessing(false);
    }
  };

  // Check if transaction is expired (older than 55 minutes - 5 min buffer before actual 1 hour expiry)
  const isTransactionExpired = (trans) => {
    if (trans.status !== 'pending') return false;
    
    // Parse created_at as UTC
    let createdAt;
    const createdAtStr = trans.created_at;
    if (createdAtStr.endsWith('Z') || createdAtStr.includes('+')) {
      createdAt = new Date(createdAtStr);
    } else {
      // Assume UTC if no timezone specified
      createdAt = new Date(createdAtStr + 'Z');
    }
    
    const now = new Date();
    const diffMinutes = (now - createdAt) / (1000 * 60);
    return diffMinutes >= 55; // 55 minutes = 1 hour - 5 min buffer
  };

  // Calculate remaining time for pending transaction (expires at 55 minutes)
  const getRemainingTime = (trans) => {
    // Parse created_at as UTC
    let createdAt;
    const createdAtStr = trans.created_at;
    if (createdAtStr.endsWith('Z') || createdAtStr.includes('+')) {
      createdAt = new Date(createdAtStr);
    } else {
      // Assume UTC if no timezone specified
      createdAt = new Date(createdAtStr + 'Z');
    }
    
    const expiry = new Date(createdAt.getTime() + 55 * 60 * 1000); // 55 minutes
    const now = new Date();
    const diffMs = expiry - now;
    
    if (diffMs <= 0) return null;
    
    const totalSeconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    if (minutes >= 60) {
      return `${Math.floor(minutes / 60)} jam ${minutes % 60} menit`;
    } else if (minutes > 0) {
      return `${minutes} menit ${seconds} detik`;
    } else {
      return `${seconds} detik`;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Menunggu', color: 'bg-yellow-100 text-yellow-800' },
      success: { label: 'Berhasil', color: 'bg-green-100 text-green-800' },
      failed: { label: 'Gagal', color: 'bg-red-100 text-red-800' },
      expired: { label: 'Kedaluwarsa', color: 'bg-gray-100 text-gray-800' },
      cancelled: { label: 'Dibatalkan', color: 'bg-gray-100 text-gray-600' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Cancel a pending transaction
  const cancelTransaction = async (transactionId) => {
    try {
      await axios.post(`${API}/topup/cancel/${transactionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transaksi berhasil dibatalkan');
      fetchData(); // Refresh list
    } catch (error) {
      console.error('Error cancelling transaction:', error);
      toast.error('Gagal membatalkan transaksi');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-ltb-br relative pb-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_bc6efd8f-02f7-4fdc-a397-f7bf259d1836/artifacts/uov20vz1_Pattern%20LTB.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}></div>
      
      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-white shadow-md py-3 sm:py-4 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">Kembali</span>
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-gray-800">Topup Token</h1>
            <div className="w-20"></div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          {/* Current Balance */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-200 text-sm mb-1">Saldo Token Anda</p>
                <p className="text-4xl font-bold">{user?.tokens_balance || 0}</p>
                <p className="text-blue-200 text-sm mt-1">Token</p>
              </div>
              <div className="bg-white/20 rounded-full p-4">
                <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Token Packages */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Pilih Paket Token</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {packages.map((pkg) => {
                const totalTokens = pkg.jumlah_token + (pkg.bonus_token || 0);
                const isSelected = selectedPackage?.id === pkg.id;
                const hasBonus = pkg.bonus_token > 0;
                
                return (
                  <button
                    key={pkg.id}
                    onClick={() => handleTopup(pkg)}
                    disabled={processing}
                    className={`relative border-2 rounded-xl p-4 text-left transition-all ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    } ${processing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {hasBonus && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                        +{pkg.bonus_token} Bonus
                      </span>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-lg text-gray-800">{pkg.nama}</p>
                        <p className="text-3xl font-bold text-blue-600 my-2">
                          {totalTokens} <span className="text-base font-normal text-gray-500">Token</span>
                        </p>
                        {hasBonus && (
                          <p className="text-xs text-gray-500">
                            {pkg.jumlah_token} + {pkg.bonus_token} bonus
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-800">{formatRupiah(pkg.harga)}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatRupiah(pkg.harga / totalTokens)}/token
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Transaction History */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Riwayat Pembelian</h2>
            {transactions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p>Belum ada riwayat pembelian</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.map((trans) => {
                  const isPending = trans.status === 'pending';
                  const isExpiredLocally = isPending && isTransactionExpired(trans);
                  const remainingTime = isPending && !isExpiredLocally ? getRemainingTime(trans) : null;
                  const canResume = isPending && !isExpiredLocally;
                  
                  return (
                    <div 
                      key={trans.id} 
                      data-testid={`transaction-${trans.id}`}
                      className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                        canResume 
                          ? 'bg-yellow-50 border-2 border-yellow-300' 
                          : isPending && isExpiredLocally
                            ? 'bg-red-50 border-2 border-red-200'
                            : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">
                          {trans.t_token_package?.nama || 'Topup Token'}
                        </p>
                        <p className="text-sm text-gray-500">{formatDate(trans.created_at)}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {trans.payment_type ? `via ${trans.payment_type}` : trans.order_id}
                        </p>
                        {canResume && remainingTime && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">
                              ⏱️ {remainingTime}
                            </span>
                          </div>
                        )}
                        {isPending && isExpiredLocally && (
                          <p className="text-xs text-red-600 font-medium mt-2">
                            ⚠️ Waktu pembayaran habis
                          </p>
                        )}
                        {/* Action buttons for pending transactions */}
                        {canResume && (
                          <div className="mt-3 flex gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResumePending(trans);
                              }}
                              className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Lanjutkan Bayar
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Yakin ingin membatalkan transaksi ini?')) {
                                  cancelTransaction(trans.id);
                                }
                              }}
                              className="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                            >
                              Batalkan
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-blue-600">+{trans.jumlah_token} Token</p>
                        <p className="text-sm text-gray-600">{formatRupiah(trans.harga)}</p>
                        {getStatusBadge(trans.status)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-medium text-yellow-800">Informasi Penting</p>
                <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                  <li>Token akan ditambahkan setelah pembayaran berhasil</li>
                  <li>Proses verifikasi pembayaran memakan waktu 1-5 menit</li>
                  <li>Hubungi admin jika ada kendala</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopupPage;
